import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.cmChatSocket = null;
    this.waitingRoomSocket = null;
    this.isConnected = false;
    this.isCmChatConnected = false;
    this.isWaitingRoomConnected = false;
    this.listeners = new Map();
    this.cmChatListeners = new Map();
    this.waitingRoomListeners = new Map();
  }

  getBackendUrl() {
    return process.env.REACT_APP_BASE_URL || 'https://provider.numente.io';
  }

  getAuthToken() {
    return localStorage.getItem('user_id');
  }

  connect() {
    if (this.socket?.connected) {
      return this.socket;
    }

    const backendUrl = this.getBackendUrl();
    
    try {
      this.socket = io(backendUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 10000,
        forceNew: false,
      });

      this.socket.on('connect', () => {
        this.isConnected = true;
        if (process.env.NODE_ENV === 'development' && this.socket?.id) {
          console.log('Socket.IO connected:', this.socket.id);
        }
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        if (process.env.NODE_ENV === 'development') {
          console.log('Socket.IO disconnected:', reason);
        }
      });

      this.socket.on('connect_error', (error) => {
        this.isConnected = false;
        // Log error in both development and production for debugging
        console.warn('Socket.IO connection error:', {
          message: error.message,
          type: error.type,
          description: error.description,
          backendUrl: backendUrl
        });
        
        // If connection fails, it will automatically retry based on reconnection settings
        // Don't throw error here - let reconnection logic handle it
      });

      if (!this.socket) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to create Socket.IO connection');
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error initializing Socket.IO:', error);
      }
      this.socket = null;
      this.isConnected = false;
    }

    return this.socket;
  }

  connectCmChat() {
    if (this.cmChatSocket?.connected) {
      return this.cmChatSocket;
    }

    const backendUrl = this.getBackendUrl();
    const token = this.getAuthToken();

    this.cmChatSocket = io(`${backendUrl}/cm-chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 10000,
      forceNew: false,
    });

    this.cmChatSocket.on('connect', () => {
      this.isCmChatConnected = true;
    });

    this.cmChatSocket.on('disconnect', () => {
      this.isCmChatConnected = false;
    });

    this.cmChatSocket.on('connect_error', (error) => {
      this.isCmChatConnected = false;
      console.warn('CM chat socket connection error:', error.message);
    });

    return this.cmChatSocket;
  }

  disconnectCmChat() {
    if (this.cmChatSocket) {
      this.cmChatSocket.disconnect();
      this.cmChatSocket = null;
      this.isCmChatConnected = false;
      this.cmChatListeners.clear();
    }
  }

  getCmChatSocket() {
    if (!this.cmChatSocket) {
      return this.connectCmChat();
    }
    return this.cmChatSocket;
  }

  emitCmChat(event, payload, ack) {
    const socket = this.getCmChatSocket();
    if (!socket) return;
    if (typeof ack === 'function') {
      socket.emit(event, payload, ack);
      return;
    }
    socket.emit(event, payload);
  }

  onCmChat(event, callback) {
    const socket = this.getCmChatSocket();
    if (!socket) return;

    const wrappedCallback = (...args) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in CM chat socket ${event} handler:`, error);
      }
    };

    socket.on(event, wrappedCallback);

    if (!this.cmChatListeners.has(event)) {
      this.cmChatListeners.set(event, []);
    }
    this.cmChatListeners.get(event).push(wrappedCallback);
  }

  offCmChat(event, callback) {
    const socket = this.getCmChatSocket();
    if (!socket) return;

    if (callback) {
      socket.off(event, callback);
      const listeners = this.cmChatListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      socket.off(event);
      this.cmChatListeners.delete(event);
    }
  }

  connectWaitingRoom() {
    if (this.waitingRoomSocket?.connected) {
      return this.waitingRoomSocket;
    }

    const backendUrl = this.getBackendUrl();
    const token = this.getAuthToken();

    this.waitingRoomSocket = io(`${backendUrl}/waiting-room`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 10000,
      forceNew: false,
    });

    this.waitingRoomSocket.on('connect', () => {
      this.isWaitingRoomConnected = true;
    });

    this.waitingRoomSocket.on('disconnect', () => {
      this.isWaitingRoomConnected = false;
    });

    this.waitingRoomSocket.on('connect_error', (error) => {
      this.isWaitingRoomConnected = false;
      console.warn('Waiting room socket connection error:', error.message);
    });

    return this.waitingRoomSocket;
  }

  disconnectWaitingRoom() {
    if (this.waitingRoomSocket) {
      this.waitingRoomSocket.disconnect();
      this.waitingRoomSocket = null;
      this.isWaitingRoomConnected = false;
      this.waitingRoomListeners.clear();
    }
  }

  getWaitingRoomSocket() {
    if (!this.waitingRoomSocket) {
      return this.connectWaitingRoom();
    }
    return this.waitingRoomSocket;
  }

  emitWaitingRoom(event, payload, ack) {
    const socket = this.getWaitingRoomSocket();
    if (!socket) return;

    if (typeof ack === 'function') {
      socket.emit(event, payload, ack);
      return;
    }

    socket.emit(event, payload);
  }

  onWaitingRoom(event, callback) {
    const socket = this.getWaitingRoomSocket();
    if (!socket) return;

    const wrappedCallback = (...args) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in waiting room socket ${event} handler:`, error);
      }
    };

    socket.on(event, wrappedCallback);

    if (!this.waitingRoomListeners.has(event)) {
      this.waitingRoomListeners.set(event, []);
    }
    this.waitingRoomListeners.get(event).push(wrappedCallback);
  }

  offWaitingRoom(event, callback) {
    const socket = this.getWaitingRoomSocket();
    if (!socket) return;

    if (callback) {
      socket.off(event, callback);
      const listeners = this.waitingRoomListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      socket.off(event);
      this.waitingRoomListeners.delete(event);
    }
  }

  disconnect() {
    this.disconnectCmChat();
    this.disconnectWaitingRoom();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  getSocket() {
    if (!this.socket) {
      this.connect();
    }
    return this.socket;
  }

  getSocketId() {
    const socket = this.getSocket();
    if (socket && socket.connected && socket.id) {
      return socket.id;
    }
    return null;
  }

  async waitForConnection(timeout = 5000) {
    return new Promise((resolve, reject) => {
      const socket = this.getSocket();
      
      if (!socket) {
        reject(new Error('Socket instance not available'));
        return;
      }
      
      if (socket.connected && socket.id) {
        resolve(socket.id);
        return;
      }

      const timeoutId = setTimeout(() => {
        socket.off('connect', connectHandler);
        socket.off('connect_error', errorHandler);
        reject(new Error(`Socket connection timeout after ${timeout}ms`));
      }, timeout);

      const connectHandler = () => {
        clearTimeout(timeoutId);
        socket.off('connect', connectHandler);
        socket.off('connect_error', errorHandler);
        
        if (socket.id) {
          resolve(socket.id);
        } else {
          reject(new Error('Socket connected but no ID available'));
        }
      };

      const errorHandler = (error) => {
        clearTimeout(timeoutId);
        socket.off('connect', connectHandler);
        socket.off('connect_error', errorHandler);
        reject(error || new Error('Socket connection failed'));
      };

      socket.on('connect', connectHandler);
      socket.on('connect_error', errorHandler);
    });
  }

  on(event, callback) {
    const socket = this.getSocket();
    
    if (!socket) {
      console.error('Socket not available for event:', event);
      return;
    }
    
    const wrappedCallback = (...args) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in Socket.IO ${event} handler:`, error);
      }
    };
    
    socket.on(event, wrappedCallback);
    
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(wrappedCallback);
  }

  off(event, callback) {
    const socket = this.getSocket();
    if (!socket) {
      return;
    }
    
    if (callback) {
      socket.off(event, callback);
      const listeners = this.listeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      socket.off(event);
      this.listeners.delete(event);
    }
  }

  removeAllListeners(event) {
    const socket = this.getSocket();
    if (!socket) {
      return;
    }
    
    if (event) {
      socket.removeAllListeners(event);
      this.listeners.delete(event);
    } else {
      socket.removeAllListeners();
      this.listeners.clear();
    }
  }
}

const socketService = new SocketService();
export default socketService;
